import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronDown, Menu, X } from 'lucide-react';

const Header = () => {
  const navigate = useNavigate();
  const [showPillarsDropdown, setShowPillarsDropdown] = useState(false);
  const [showAgentLibraryDropdown, setShowAgentLibraryDropdown] = useState(false);
  const [hoveredCategory, setHoveredCategory] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileAgentLibraryOpen, setMobileAgentLibraryOpen] = useState(false);
  const [mobilePlatformSolutionsOpen, setMobilePlatformSolutionsOpen] = useState(false);
  const [mobileExpandedCategory, setMobileExpandedCategory] = useState(null);
  const timeoutRef = useRef(null);
  const agentTimeoutRef = useRef(null);

  const handleBookDemo = () => {
    window.open('https://calendly.com/speakmulti/introduction', '_blank');
  };

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const headerOffset = 100; // Account for fixed header height + padding
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
    setShowPillarsDropdown(false);
  };

  const handleMouseEnter = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    setShowPillarsDropdown(true);
  };

  const handleMouseLeave = () => {
    timeoutRef.current = setTimeout(() => {
      setShowPillarsDropdown(false);
    }, 100);
  };

  const handleAgentLibraryEnter = () => {
    if (agentTimeoutRef.current) {
      clearTimeout(agentTimeoutRef.current);
    }
    setShowAgentLibraryDropdown(true);
  };

  const handleAgentLibraryLeave = () => {
    agentTimeoutRef.current = setTimeout(() => {
      setShowAgentLibraryDropdown(false);
      setHoveredCategory(null);
    }, 100);
  };

  const agentCategories = {
    'Sales': {
      route: '/agent-library/sales',
      agents: [
        { name: 'Lead Qualification Scoring Agent', route: '/agent-library/sales/lead-qualification' },
        { name: 'Lead Assignment Agent', route: '/agent-library/sales/lead-assignment' },
        { name: 'Sales Order Creation and Validation Agent', route: '/agent-library/sales/sales-order-creation' },
        { name: 'Quote Generation Agent', route: '/agent-library/sales/quote-generation' }
      ]
    },
    'Customer Service': {
      route: '/agent-library/customer-service',
      agents: [
        { name: 'Customer Support Email Responder Agent', route: '/agent-library/customer-service/email-responder' },
        { name: 'Smart Follow-Up Email Agent', route: '/agent-library/customer-service/follow-up-email' },
        { name: 'Dynamic Query Resolution Agent', route: '/agent-library/customer-service/query-resolution' }
      ]
    },
    'Marketing': {
      route: '/agent-library/marketing',
      agents: [
        { name: 'Content Research AI Agent', route: '/agent-library/marketing/content-research' },
        { name: 'Brand Voice Analyzer Agent', route: '/agent-library/marketing/brand-voice-analyzer' },
        { name: 'Social Media Content Generator Agent', route: '/agent-library/marketing/social-media-generator' }
      ]
    },
    'Operations': {
      route: '/agent-library/operations',
      agents: [
        { name: 'Order Entry Management Agent', route: '/agent-library/operations/order-entry' },
        { name: 'Technical Language Interpreter AI Agent', route: '/agent-library/operations/tech-language-interpreter' },
        { name: 'Healthcare Care Follow-Up and Notification Agent', route: '/agent-library/operations/care-follow-up' },
        { name: 'Credit Card Origination Agent', route: '/agent-library/operations/credit-card-origination' }
      ]
    }
  };

  const handleCategoryClick = (category) => {
    const categoryData = agentCategories[category];
    if (categoryData && categoryData.route) {
      navigate(categoryData.route);
      setShowAgentLibraryDropdown(false);
      setHoveredCategory(null);
      setMobileMenuOpen(false);
      setMobileAgentLibraryOpen(false);
      setMobileExpandedCategory(null);
    }
  };

  const handleAgentClick = (route) => {
    navigate(route);
    setMobileMenuOpen(false);
    setMobileAgentLibraryOpen(false);
    setMobileExpandedCategory(null);
    setShowAgentLibraryDropdown(false);
  };

  const toggleMobileCategoryExpand = (category) => {
    setMobileExpandedCategory(mobileExpandedCategory === category ? null : category);
  };

  return (
    <header className="light-header">
      <div className="flex items-center gap-3">
        <img 
          src="https://customer-assets.emergentagent.com/job_oneplace-redesign/artifacts/rt4rgdof_oneplace_white.png"
          alt="OnePlace"
          className="h-7 object-contain cursor-pointer hover:opacity-80 transition-opacity"
          onClick={() => navigate('/')}
        />
      </div>
      
      {/* Desktop Navigation */}
      <nav className="light-nav desktop-nav">
        {/* Agent Library Dropdown with Nested Menus */}
        <div 
          className="relative dropdown-wrapper"
          onMouseEnter={handleAgentLibraryEnter}
          onMouseLeave={handleAgentLibraryLeave}
        >
          <button 
            className="light-nav-link flex items-center gap-1"
          >
            Agent Library
            <ChevronDown className="w-4 h-4" />
          </button>
          
          {showAgentLibraryDropdown && (
            <div className="absolute top-full left-0 pt-2 w-64 z-50">
              <div className="bg-[var(--bg-secondary)] border border-[var(--border-subtle)] rounded-lg shadow-lg backdrop-filter backdrop-blur-lg">
                {Object.keys(agentCategories).map((category, idx) => (
                  <div 
                    key={category}
                    className="relative group"
                    onMouseEnter={() => setHoveredCategory(category)}
                  >
                    <div 
                      onClick={() => handleCategoryClick(category)}
                      className={`flex items-center justify-between px-4 py-3 text-[var(--text-primary)] hover:bg-[var(--bg-overlay)] transition-colors cursor-pointer ${
                        idx === 0 ? 'rounded-t-lg' : ''
                      } ${idx === Object.keys(agentCategories).length - 1 && hoveredCategory !== category ? 'rounded-b-lg' : ''} ${
                        idx !== 0 ? 'border-t border-[var(--border-subtle)]' : ''
                      }`}
                    >
                      <span>{category}</span>
                      <ChevronDown className="w-4 h-4 rotate-[-90deg]" />
                    </div>
                    
                    {/* Nested Dropdown */}
                    {hoveredCategory === category && (
                      <div className="absolute left-full top-0 ml-1 w-80 z-50">
                        <div className="bg-[var(--bg-secondary)] border border-[var(--border-subtle)] rounded-lg shadow-lg backdrop-filter backdrop-blur-lg">
                          {agentCategories[category].agents.map((agent, agentIdx) => (
                            <div
                              key={agentIdx}
                              onClick={() => {
                                if (agent.route) {
                                  handleAgentClick(agent.route);
                                } else {
                                  handleCategoryClick(category);
                                }
                              }}
                              className={`px-4 py-3 text-[var(--text-primary)] hover:bg-[var(--bg-overlay)] transition-colors cursor-pointer text-sm ${
                                agentIdx === 0 ? 'rounded-t-lg' : ''
                              } ${agentIdx === agentCategories[category].agents.length - 1 ? 'rounded-b-lg' : ''} ${
                                agentIdx !== 0 ? 'border-t border-[var(--border-subtle)]' : ''
                              }`}
                            >
                              {agent.name}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div 
          className="relative dropdown-wrapper"
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          <button 
            className="light-nav-link flex items-center gap-1"
          >
            Platform Solutions
            <ChevronDown className="w-4 h-4" />
          </button>
          
          {showPillarsDropdown && (
            <div className="absolute top-full left-0 pt-2 w-64 z-50">
              <div className="bg-[var(--bg-secondary)] border border-[var(--border-subtle)] rounded-lg shadow-lg backdrop-filter backdrop-blur-lg">
                <a 
                  href="#text-to-sql" 
                  className="block px-4 py-3 text-[var(--text-primary)] hover:bg-[var(--bg-overlay)] transition-colors rounded-t-lg"
                  onClick={(e) => { e.preventDefault(); scrollToSection('text-to-sql'); }}
                >
                  OnePlace NLQ Engine
                </a>
                <a 
                  href="#tool-management" 
                  className="block px-4 py-3 text-[var(--text-primary)] hover:bg-[var(--bg-overlay)] transition-colors border-t border-[var(--border-subtle)]"
                  onClick={(e) => { e.preventDefault(); scrollToSection('tool-management'); }}
                >
                  OnePlace Tool Management
                </a>
                <a 
                  href="#agent-workflows" 
                  className="block px-4 py-3 text-[var(--text-primary)] hover:bg-[var(--bg-overlay)] transition-colors border-t border-[var(--border-subtle)]"
                  onClick={(e) => { e.preventDefault(); scrollToSection('agent-workflows'); }}
                >
                  OnePlace Agent Builder
                </a>
                <a 
                  href="#agent-workflows" 
                  className="block px-4 py-3 text-[var(--text-primary)] hover:bg-[var(--bg-overlay)] transition-colors border-t border-[var(--border-subtle)] rounded-b-lg"
                  onClick={(e) => { e.preventDefault(); scrollToSection('agent-workflows'); }}
                >
                  OnePlace Agent Observability
                </a>
              </div>
            </div>
          )}
        </div>
        
        <button 
          onClick={handleBookDemo}
          className="btn-primary light-button-animate"
        >
          Book a Demo
        </button>
      </nav>

      {/* Mobile Hamburger Button */}
      <button 
        className="mobile-menu-button"
        onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
        aria-label="Toggle menu"
      >
        {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
      </button>

      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div className="mobile-menu-overlay" onClick={() => setMobileMenuOpen(false)}>
          <div className="mobile-menu" onClick={(e) => e.stopPropagation()}>
            {/* Agent Library Section */}
            <div className="mobile-menu-section">
              <button 
                className="mobile-menu-item mobile-menu-header"
                onClick={() => setMobileAgentLibraryOpen(!mobileAgentLibraryOpen)}
              >
                <span>Agent Library</span>
                <ChevronDown className={`w-5 h-5 transition-transform ${mobileAgentLibraryOpen ? 'rotate-180' : ''}`} />
              </button>
              
              {mobileAgentLibraryOpen && (
                <div className="mobile-submenu">
                  {Object.keys(agentCategories).map((category) => (
                    <div key={category} className="mobile-category">
                      <button 
                        className="mobile-category-header"
                        onClick={() => toggleMobileCategoryExpand(category)}
                      >
                        <span>{category}</span>
                        <ChevronDown className={`w-4 h-4 transition-transform ${mobileExpandedCategory === category ? 'rotate-180' : ''}`} />
                      </button>
                      
                      {mobileExpandedCategory === category && (
                        <div className="mobile-agent-list">
                          {agentCategories[category].agents.map((agent, idx) => (
                            <button
                              key={idx}
                              className="mobile-agent-item"
                              onClick={() => handleAgentClick(agent.route)}
                            >
                              {agent.name}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Platform Solutions Section */}
            <div className="mobile-menu-section">
              <button 
                className="mobile-menu-item mobile-menu-header"
                onClick={() => setMobilePlatformSolutionsOpen(!mobilePlatformSolutionsOpen)}
              >
                <span>Platform Solutions</span>
                <ChevronDown className={`w-5 h-5 transition-transform ${mobilePlatformSolutionsOpen ? 'rotate-180' : ''}`} />
              </button>
              
              {mobilePlatformSolutionsOpen && (
                <div className="mobile-submenu">
                  <button 
                    className="mobile-submenu-item"
                    onClick={() => {
                      scrollToSection('text-to-sql');
                      setMobileMenuOpen(false);
                    }}
                  >
                    OnePlace NLQ Engine
                  </button>
                  <button 
                    className="mobile-submenu-item"
                    onClick={() => {
                      scrollToSection('tool-management');
                      setMobileMenuOpen(false);
                    }}
                  >
                    OnePlace Tool Management
                  </button>
                  <button 
                    className="mobile-submenu-item"
                    onClick={() => {
                      scrollToSection('agent-workflows');
                      setMobileMenuOpen(false);
                    }}
                  >
                    OnePlace Agent Builder
                  </button>
                  <button 
                    className="mobile-submenu-item"
                    onClick={() => {
                      scrollToSection('agent-workflows');
                      setMobileMenuOpen(false);
                    }}
                  >
                    OnePlace Agent Observability
                  </button>
                </div>
              )}
            </div>

            {/* Book a Demo Button */}
            <button 
              onClick={() => {
                handleBookDemo();
                setMobileMenuOpen(false);
              }}
              className="btn-primary mobile-cta-button"
            >
              Book a Demo
            </button>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;